import { ClientContact } from "@jugl-web/rest-api";
import { PlainButton } from "@jugl-web/ui-components";
import { cx, useTranslations } from "@jugl-web/utils";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import {
  ContactDetailsFormFields,
  ContactDetailsFormValues,
} from "@web-src/modules/client-support/components/ContactDetailsFormFields";
import React from "react";
import { useFormContext } from "react-hook-form";
import { collapsedIconButtonClassName } from "../../../../consts";
import { ReactComponent as ArrowLeftIcon } from "../../assets/arrow-left.svg";
import { ReactComponent as ArrowRightIcon } from "../../assets/arrow-right.svg";
import { IdentifyContactDialogFooter } from "../../components/IdentifyContactDialogFooter";
import { useIdentifyContact } from "../../hooks/useIdentifyContact";
import { IdentifyContactDialogScreenMap } from "../../types";

export const CreateContactScreen: React.FC<{
  onRequestClose: () => void;
  onContactIdentified?: (contact: ClientContact) => void;
}> = ({ onRequestClose, onContactIdentified }) => {
  const { t } = useTranslations();
  const { transitionTo } =
    useScreenTransitionManager<IdentifyContactDialogScreenMap>();
  const { handleCreateContact, isSubmitting } = useIdentifyContact();

  const { handleSubmit } = useFormContext<ContactDetailsFormValues>();

  const onSubmit = handleSubmit(async (values) => {
    const identifiedContact = await handleCreateContact(values);

    if (identifiedContact) {
      if (onContactIdentified) {
        onContactIdentified(identifiedContact);
      } else {
        onRequestClose();
      }
    }
  });

  return (
    <>
      <div className="flex flex-col gap-8 px-14 pt-10 pb-12">
        <div className="flex gap-4">
          <PlainButton
            className={cx(collapsedIconButtonClassName, "h-8 w-8")}
            onClick={() => transitionTo({ name: "linkExistingContact" })}
          >
            <ArrowLeftIcon />
          </PlainButton>
          <div className="w-px shrink-0 rounded-full bg-[#E0E0E0]" />
          <div className="flex flex-col gap-1">
            <span className="font-secondary font-medium text-dark">
              {t({
                id: "client-support-page.enter-customer-details",
                defaultMessage: "Enter Customer Details",
              })}
            </span>
            <span className="font-secondary text-[13px] text-dark-600">
              {t({
                id: "client-support-page.email-or-phone-required",
                defaultMessage:
                  "An email or Phone number is required to create a new customer",
              })}
            </span>
          </div>
        </div>
        <ContactDetailsFormFields />
      </div>
      <IdentifyContactDialogFooter
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            variant: "tertiary",
            className: "w-[120px]",
            onClick: onRequestClose,
            isDisabled: isSubmitting,
          },
          {
            text: t({
              id: "client-support-page.add-customer",
              defaultMessage: "Add Customer",
            }),
            className: "w-[300px]",
            iconEnd: <ArrowRightIcon />,
            onClick: onSubmit,
            isDisabled: isSubmitting,
          },
        ]}
      />
    </>
  );
};
